import { ButtonCoreSize, ButtonCoreVariant } from './ButtonCore';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

export const BUTTON_FOCUS_SHADOW_DISTANCE = 1;
export const BUTTON_FOCUS_SHADOW_WIDTH = 2;

export const BUTTON_WITH_ICON_SPACING_OFFSET = 8;

interface ButtonDimensions {
  height: number;
  hpad: number;
  iconDiameter: number;
}

export const buttonBoxShadows = {
  dropShadowNone: '0 1px 0 0 rgba(0, 0, 0, 0)',
  innerShadow01: 'inset 0 2px 0 0 rgba(0, 0, 0, 0.02)',
  innerShadow03: 'inset 0 -2px 0 0 rgba(0, 0, 0, 0.2)',
  innerShadow04: 'inset 0 2px 0 0 rgba(0, 0, 0, 0.2)',
};

export const buttonCoreBase = (theme: Theme, variant: ButtonCoreVariant) => [
  css`
    align-items: center;
    border: ${variant === 'basic'
      ? `${BUTTON_FOCUS_SHADOW_DISTANCE}px solid ${theme.colors.borderDefault}`
      : 'none'};
    box-sizing: border-box;
    display: inline-flex;
    font-family: ${theme.typography.fontFamily};
    font-weight: ${theme.typography.fontWeightMedium};
    justify-content: center;
    outline: none;
    position: relative;
    transition: all ${theme.timings.duration.shortest} ${theme.timings.timingFunction.easeInOut};
    vertical-align: middle;
    z-index: initial;

    a& {
      text-decoration: none;
    }

    button& {
      appearance: none;
      &:not(:disabled):not([aria-disabled='true']) {
        cursor: pointer;
      }
    }
  `,
  buttonBaseFocusContent(theme),
];

export const buttonBaseFocusContent = ({ colors }: Theme) => css`
  &::after {
    bottom: calc(-1 * (${BUTTON_FOCUS_SHADOW_WIDTH}px + ${BUTTON_FOCUS_SHADOW_DISTANCE}px));
    box-shadow: 0 0 0 ${BUTTON_FOCUS_SHADOW_WIDTH}px transparent;
    content: '';
    left: calc(-1 * (${BUTTON_FOCUS_SHADOW_WIDTH}px + ${BUTTON_FOCUS_SHADOW_DISTANCE}px));
    pointer-events: none;
    position: absolute;
    right: calc(-1 * (${BUTTON_FOCUS_SHADOW_WIDTH}px + ${BUTTON_FOCUS_SHADOW_DISTANCE}px));
    top: calc(-1 * (${BUTTON_FOCUS_SHADOW_WIDTH}px + ${BUTTON_FOCUS_SHADOW_DISTANCE}px));
  }
`;

export const buttonCoreVariant = (
  theme: Theme,
  buttonVariant: ButtonCoreVariant,
  selected?: boolean
) => [
  buttonThemeFromButtonAndInteractionTypes(theme, buttonVariant, 'enabled', selected),
  theme.interactiveStates.hover(
    buttonThemeFromButtonAndInteractionTypes(theme, buttonVariant, 'hover', selected)
  ),
  theme.interactiveStates.focus(css`
    ${buttonThemeFromButtonAndInteractionTypes(theme, buttonVariant, 'focus', selected)};
    box-shadow: ${buttonBoxShadows.dropShadowNone};
    z-index: ${theme.zIndex.bump};
    &::after {
      box-shadow: 0 0 0 ${BUTTON_FOCUS_SHADOW_WIDTH}px ${theme.colors.borderPrimaryBase};
    }
  `),
  theme.interactiveStates.active(css`
    ${buttonThemeFromButtonAndInteractionTypes(theme, buttonVariant, 'active', selected)}

    & > * {
      transform: translateY(1px);
    }
  `),
  theme.interactiveStates.disabled(
    buttonThemeFromButtonAndInteractionTypes(theme, buttonVariant, 'disabled', selected)
  ),
];

export const buttonDimensionsFromSize = (
  theme: Theme,
  buttonSize: ButtonCoreSize
): ButtonDimensions => {
  switch (buttonSize) {
    case 'xlarge':
      return {
        height: getButtonHeightForSize('xlarge'),
        hpad: getButtonHpadForSize('xlarge'),
        iconDiameter: getButtonIconDiameterForSize('xlarge'),
      };
    case 'large':
      return {
        height: getButtonHeightForSize('large'),
        hpad: getButtonHpadForSize('large'),
        iconDiameter: getButtonIconDiameterForSize('large'),
      };
    case 'medium':
      return {
        height: getButtonHeightForSize('medium'),
        hpad: getButtonHpadForSize('medium'),
        iconDiameter: getButtonIconDiameterForSize('medium'),
      };
    case 'small':
      return {
        height: getButtonHeightForSize('small'),
        hpad: getButtonHpadForSize('small'),
        iconDiameter: getButtonIconDiameterForSize('small'),
      };
    case 'xsmall':
      return {
        height: getButtonHeightForSize('xsmall'),
        hpad: getButtonHpadForSize('xsmall'),
        iconDiameter: getButtonIconDiameterForSize('xsmall'),
      };
    default:
      assertNever(buttonSize);
  }
};

const getButtonTypographyForSize = ({ typography }: Theme, buttonSize: ButtonCoreSize) => {
  switch (buttonSize) {
    case 'xlarge':
      return typography.variant('body01Strong');
    case 'large':
    case 'medium':
    case 'small':
      return typography.variant('body02Strong');
    case 'xsmall':
      return typography.variant('captionStrong');
    default:
      assertNever(buttonSize);
  }
};

export const getButtonHeightForSize = (buttonSize: ButtonCoreSize) => {
  switch (buttonSize) {
    case 'xlarge':
      return 56;
    case 'large':
      return 48;
    case 'medium':
      return 44;
    case 'small':
      return 36;
    case 'xsmall':
      return 28;
    default:
      assertNever(buttonSize);
  }
};

export const getButtonHpadForSize = (buttonSize: ButtonCoreSize) => {
  switch (buttonSize) {
    case 'xlarge':
      return 32;
    case 'large':
      return 24;
    case 'medium':
      return 24;
    case 'small':
      return 20;
    case 'xsmall':
      return 16;
    default:
      assertNever(buttonSize);
  }
};

export const getButtonIconDiameterForSize = (buttonSize: ButtonCoreSize) => {
  switch (buttonSize) {
    case 'xlarge':
      return 28;
    case 'large':
      return 24;
    case 'medium':
      return 24;
    case 'small':
      return 20;
    case 'xsmall':
      return 16;
    default:
      assertNever(buttonSize);
  }
};

export const buttonCoreSize = (theme: Theme, buttonSize: ButtonCoreSize) => {
  const { height, hpad } = buttonDimensionsFromSize(theme, buttonSize);
  return css`
    ${getButtonTypographyForSize(theme, buttonSize)}
    border-radius: ${height}px;
    height: ${height}px;
    line-height: ${height}px;
    padding: 0 ${hpad}px;

    &::after {
      border-radius: ${height}px;
    }
  `;
};

const buttonThemeFromButtonAndInteractionTypes = (
  theme: Theme,
  buttonVariant: ButtonCoreVariant,
  buttonInteractionType: ButtonInteractionType,
  selected?: boolean
) => {
  switch (buttonVariant) {
    case 'basic':
      return buttonThemeInteractionBasic(theme, buttonInteractionType, selected);
    case 'basicBorderless':
      return buttonThemeInteractionBasicBorderless(theme, buttonInteractionType);
    case 'ghost':
      return buttonThemeInteractionGhost(theme, buttonInteractionType);
    case 'primary':
      return buttonThemeInteractionPrimary(theme, buttonInteractionType);
    case 'danger':
      return buttonThemeInteractionDanger(theme, buttonInteractionType);
    case 'success':
      return buttonThemeInteractionSuccess(theme, buttonInteractionType);
    case 'highContrast':
      return buttonThemeInteractionHighContrast(theme, buttonInteractionType);
    case 'ghostInverted':
      return buttonThemeInteractionGhostInverted(theme, buttonInteractionType);
    default:
      assertNever(buttonVariant);
  }
};

export type ButtonInteractionType = 'enabled' | 'hover' | 'focus' | 'active' | 'disabled';

export const buttonThemeInteractionBasic = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType,
  selected?: boolean
) => {
  const { shadow, colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: ${colors.bgDefault};
        border-color: ${selected ? colors.borderDark : colors.borderDefault};
        box-shadow: ${shadow[100]};
        color: ${getEnabledButtonColorFromVariant(theme, 'basic')};
        fill: ${colors.fgDefault};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgLighter};
        border-color: ${selected ? colors.borderDark : colors.borderDefault};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'basic')};
        border-color: ${selected ? colors.borderDark : colors.borderDefault};
        box-shadow: ${buttonBoxShadows.innerShadow01};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        border-color: ${colors.borderDefault};
        box-shadow: ${buttonBoxShadows.innerShadow01};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

const buttonThemeInteractionBasicBorderless = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { shadow, colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: ${colors.bgDefault};
        box-shadow: ${shadow[100]};
        color: ${getEnabledButtonColorFromVariant(theme, 'basicBorderless')};
        fill: ${colors.fgDefault};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgLighter};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'basicBorderless')};
        box-shadow: ${buttonBoxShadows.innerShadow01};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

const buttonThemeInteractionGhost = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: transparent;
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${getEnabledButtonColorFromVariant(theme, 'ghost')};
        fill: ${colors.fgDefault};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgLighter};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'ghost')};
        box-shadow: ${buttonBoxShadows.innerShadow01};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

export const buttonThemeInteractionPrimary = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { shadow, colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: ${colors.bgPrimaryBase};
        box-shadow: ${shadow[100]};
        color: ${getEnabledButtonColorFromVariant(theme, 'primary')};
        fill: ${colors.fgContrast};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgPrimaryDark};
        box-shadow: ${shadow[100]};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'primary')};
        box-shadow: ${buttonBoxShadows.innerShadow04};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

const buttonThemeInteractionDanger = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: transparent;
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${getEnabledButtonColorFromVariant(theme, 'danger')};
        fill: ${colors.fgDangerBase};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgDangerLighter};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'danger')};
        box-shadow: ${buttonBoxShadows.innerShadow01};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

export const buttonThemeInteractionSuccess = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { shadow, colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: ${colors.bgSuccessBase};
        box-shadow: ${shadow[100]};
        color: ${getEnabledButtonColorFromVariant(theme, 'success')};
        fill: ${colors.fgContrast};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgSuccessBase};
        box-shadow: ${buttonBoxShadows.innerShadow03}, ${shadow[100]};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'success')};
        box-shadow: ${buttonBoxShadows.innerShadow04};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

const buttonThemeInteractionHighContrast = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { shadow, colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: ${colors.bgDarker};
        box-shadow: ${shadow};
        color: ${getEnabledButtonColorFromVariant(theme, 'highContrast')};
        fill: ${colors.fgContrast};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgContrast};
        box-shadow: ${buttonBoxShadows.innerShadow03}, ${shadow[100]};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'highContrast')};
        box-shadow: ${buttonBoxShadows.innerShadow04};
      `;
    case 'disabled':
      return css`
        background-color: ${colors.bgLighter};
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

const buttonThemeInteractionGhostInverted = (
  theme: Theme,
  buttonInteractionType: ButtonInteractionType
) => {
  const { colors } = theme;
  switch (buttonInteractionType) {
    case 'enabled':
      return css`
        background-color: transparent;
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${getEnabledButtonColorFromVariant(theme, 'ghostInverted')};
        fill: ${colors.fgContrast};
      `;
    case 'focus':
      return css``;
    case 'hover':
      return css`
        background-color: ${colors.bgContrast};
      `;
    case 'active':
      return css`
        background-color: ${getActiveButtonColorFromVariant(theme, 'ghostInverted')};
        box-shadow: ${buttonBoxShadows.innerShadow01};
      `;
    case 'disabled':
      return css`
        background-color: transparent;
        box-shadow: ${buttonBoxShadows.dropShadowNone};
        color: ${colors.fgDisabled};
        fill: ${colors.fgDisabled};
      `;
    default:
      assertNever(buttonInteractionType);
  }
};

export const getEnabledButtonColorFromVariant = ({ colors }: Theme, variant: ButtonCoreVariant) => {
  switch (variant) {
    case 'basic':
    case 'basicBorderless':
    case 'ghost':
      return colors.fgDefault;
    case 'primary':
    case 'success':
    case 'highContrast':
    case 'ghostInverted':
      return colors.fgContrast;
    case 'danger':
      return colors.fgDangerBase;
    default:
      assertNever(variant);
  }
};

export const getActiveButtonColorFromVariant = ({ colors }: Theme, variant: ButtonCoreVariant) => {
  switch (variant) {
    case 'basic':
    case 'basicBorderless':
      return colors.bgLight;
    case 'ghost':
      return colors.bgLight;
    case 'primary':
      return colors.bgPrimaryDark;
    case 'danger':
      return colors.bgDangerLighter;
    case 'success':
      return colors.bgSuccessBase;
    case 'highContrast':
      return colors.bgContrast;
    case 'ghostInverted':
      return colors.bgContrast;
    default:
      assertNever(variant);
  }
};

export const buttonContent = css`
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type ButtonIconPosition = 'start' | 'only' | 'end' | 'farRight';

const buttonIconPositionStyleFromPosition = ({ spacing }: Theme, position: ButtonIconPosition) => {
  switch (position) {
    case 'start':
      return css`
        margin-right: ${spacing(1)}px;
      `;
    case 'end':
      return css`
        margin-left: ${spacing(1)}px;
      `;
    case 'farRight':
      return css`
        position: absolute;
        right: ${spacing(1)}px;
        width: ${spacing(3.5)}px;
      `;
    case 'only': {
      return null;
    }
    default:
      assertNever(position);
  }
};

export const buttonIcon = (
  theme: Theme,
  {
    position,
    size,
  }: {
    position: ButtonIconPosition;
    size: ButtonCoreSize;
  }
) => {
  const positionStyle = buttonIconPositionStyleFromPosition(theme, position);
  const { iconDiameter } = buttonDimensionsFromSize(theme, size);
  return css`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    justify-content: center;
    width: ${iconDiameter}px;
    ${positionStyle}
    & svg {
      display: block;
      height: auto;
      width: 100%;
    }
  `;
};

export const buttonIconEnd = (theme: Theme, size: ButtonCoreSize) => {
  const { hpad } = buttonDimensionsFromSize(theme, size);
  return css`
    padding-right: calc(${hpad}px - ${BUTTON_WITH_ICON_SPACING_OFFSET}px);
  `;
};

export const buttonIconOnly = (theme: Theme, size: ButtonCoreSize) => {
  const { height } = buttonDimensionsFromSize(theme, size);
  return css`
    /* NOTE: This overrides core size padding styles and must come second. */
    flex: 0 0 auto;
    padding-left: 0;
    padding-right: 0;
    width: ${height}px;
  `;
};

export const buttonFullWidth = css`
  width: 100%;
`;

export const buttonSquared = css`
  border-radius: 0;
`;

export const buttonDynamicContent = (theme: Theme, disabled: boolean, hasChildren: boolean) => css`
  color: ${hasChildren || disabled ? 'inherit' : theme.colors.fgSubtle};

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.borderDefault};
    margin-right: ${theme.spacing(1.5)}px;
    padding-right: ${theme.spacing(1.5)}px;
  }
`;
